// The DOM half of the chart furniture: the `#furniture` host and the
// published lengths a DOM piece reads (R548, ui-map-interface.md §17).
//
// The cards of words — the cartouche plate and the legend card — are DOM, because §1's
// canvas/DOM split says words are DOM. `Overlay` builds the host element
// (`#furniture` inside `#ui`) and hands it over. Every card stands in the
// bottom-right stack of `ui/chart/stack.ts` on the corner grid (R1804),
// which reads the lengths `ui/layout.ts` publishes on `#ui`: `--topbar-h`,
// `--dock-h`, `--dock-w` and `--mapbtns-t`. Nothing here restates a height
// or a width.
//
// The host takes no pointer: it lies over the whole map, and a card that
// wants a press sets `pointer-events: auto` on itself.

/**
 * One published length of `#ui`, in px.
 *
 * The inline style is read first, because `ui/layout.ts` writes these with
 * `style.setProperty` and jsdom resolves no custom property through
 * `getComputedStyle`. A browser answers the same figure either way.
 */
export function cssVarPx(name: string, fallback = 0): number {
  if (typeof document === 'undefined') return fallback;
  const ui = document.getElementById('ui');
  if (!ui) return fallback;
  let raw = ui.style.getPropertyValue(name);
  if (!raw && typeof getComputedStyle === 'function') {
    raw = getComputedStyle(ui).getPropertyValue(name);
  }
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

/** A card of the chart furniture: the host's own class, and nothing else. */
export function chartCard(extra = ''): HTMLElement {
  const el = document.createElement('div');
  el.className = extra ? `chart-card ${extra}` : 'chart-card';
  return el;
}

/**
 * The host itself: shown or hidden, and at the fade of the reading change.
 *
 * `off` hides the host, so a DOM piece costs no layout at all while the
 * reader has the furniture switched off (§10.3).
 */
export function setHostVisible(root: HTMLElement | null, on: boolean): void {
  if (root) root.hidden = !on;
}

/** The fade of a reading change, over every card in the host at once. */
export function setHostAlpha(root: HTMLElement | null, a: number): void {
  if (root) root.style.opacity = a >= 1 ? '' : String(a);
}
